import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import { toast } from "sonner";
import { updateUploadZip } from "@/lib/api";

/**
 * Inline ZIP prompt shown right after checkout for buyers who uploaded
 * without a ZIP. Live market-profile lookup updates as they type.
 *
 * Props:
 *   uploadId      — string (required)
 *   onApplied?    — () => void, called after successful PATCH
 *   onDismissed?  — () => void, called when buyer skips
 */
export default function PostCheckoutZipPrompt({ uploadId, onApplied, onDismissed }) {
    const [zip, setZip] = useState("");
    const [lookup, setLookup] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (zip.length !== 5) {
            setLookup(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_BACKEND_URL}/api/cost-engine/market-lookup?zip_code=${zip}`,
                );
                if (!res.ok) return;
                const j = await res.json();
                if (!cancelled) setLookup(j);
            } catch {
                if (!cancelled) setLookup(null);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [zip]);

    const apply = async () => {
        if (zip.length !== 5) return;
        try {
            setSaving(true);
            const r = await updateUploadZip(uploadId, zip);
            toast.success(
                r.recomputed
                    ? `Localized to ${r.market_profile_name}.`
                    : `Saved ZIP ${zip}.`,
            );
            onApplied?.(r);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    "Could not apply ZIP — please try again.",
            );
        } finally {
            setSaving(false);
        }
    };

    const TIER_LABEL = {
        high_cost: "High-cost market",
        above_avg: "Above-average market",
        average: "Average market",
        lower_cost: "Lower-cost market",
    };

    return (
        <div
            className="bl-card p-5 md:p-6 mt-8 border-bl"
            data-testid="post-checkout-zip-prompt"
        >
            <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-bl-primary mt-1 shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="font-display text-lg text-bl">
                        One last thing — what's the property ZIP?
                    </div>
                    <p className="mt-1.5 text-sm text-bl-muted leading-relaxed">
                        Adding the ZIP localizes every repair-cost range in your
                        report to actual labor + material pricing for that
                        market. Takes 5 seconds.
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                        <input
                            type="text"
                            inputMode="numeric"
                            maxLength={5}
                            value={zip}
                            onChange={(e) =>
                                setZip(
                                    e.target.value
                                        .replace(/\D/g, "")
                                        .slice(0, 5),
                                )
                            }
                            placeholder="ZIP"
                            className="px-3 py-2.5 bg-bl-bg rounded-lg border border-bl text-bl text-base outline-none focus:border-bl-primary font-mono-bl w-28 tracking-widest"
                            data-testid="post-checkout-zip-input"
                            autoFocus
                        />
                        <button
                            type="button"
                            onClick={apply}
                            disabled={zip.length !== 5 || saving}
                            className="bl-btn-primary"
                            data-testid="post-checkout-zip-apply-btn"
                        >
                            {saving ? "Localizing…" : "Apply ZIP"}
                        </button>
                        <button
                            type="button"
                            onClick={onDismissed}
                            className="bl-btn-ghost text-sm"
                            data-testid="post-checkout-zip-skip-btn"
                        >
                            Skip for now
                        </button>
                    </div>
                    {lookup?.profile && (
                        <div
                            className="mt-3 text-xs text-bl-muted"
                            data-testid="post-checkout-zip-lookup-result"
                        >
                            <span className="text-bl">
                                {lookup.profile.name}
                            </span>{" "}
                            ·{" "}
                            {TIER_LABEL[lookup.profile.tier] ||
                                lookup.profile.tier}
                            {!lookup.known && (
                                <span className="ml-1 italic">
                                    (estimated — ZIP not in our local-cost map yet)
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
